<template>
  <!-- One level in focus (recipient polish 2026-11-03, DESIGN §18): the built
       sky drawing, one level at a time, large and centred. The levels snap in
       place (scroll-snap, the ▲ ▼ arrows, or the row of level glyphs); the
       ones above and below stay in view, dimmed and smaller. Each star carries
       its step number; the words are in the panel. A tap on a star focuses
       its row there. -->
  <div class="qlv" :class="{ rm: reduced }">
    <div class="qlv-strip" role="list" aria-label="Your constellations" data-testid="quest-strip">
      <button v-for="it in strip" :key="it.key" role="listitem" type="button"
              :class="['qlv-sg', it.cls, { focus: it.key === focus, bright: bright && it.cls === 'done' }]"
              :data-testid="`strip-${it.key}`" :aria-label="`${it.name}${it.cls === 'done' ? ', drawn' : it.cls === 'cur' ? ', you are here' : ''}`"
              :aria-current="it.key === focus ? 'true' : null" @click="$emit('focus', it.key)">
        <svg viewBox="0 0 32 32" aria-hidden="true" v-html="miniGlyph(it.key, it.on)"></svg>
        <span v-if="it.seed" class="qlv-seed" aria-hidden="true"></span>
      </button>
    </div>
    <div class="qlv-stage">
      <div class="qlv-arrows">
        <button type="button" data-testid="quest-level-up" aria-label="Next level" :disabled="at >= order.length - 1" @click="step(1)">▲</button>
        <button type="button" data-testid="quest-level-down" aria-label="Previous level" :disabled="at <= 0" @click="step(-1)">▼</button>
      </div>
      <div ref="car" class="qlv-car qsky" :class="{ rm: reduced }" data-testid="quest-sky" @scroll.passive="onScroll" @click="onClick"
           @keydown.enter.prevent="onClick" @keydown.space.prevent="onClick">
        <div class="qlv-pad"></div>
        <div v-for="k in topDown" :key="k" :class="['qlv-item', { focus: k === focus, justdone: k === justDone }]" :data-qitem="k"
             :data-testid="`quest-level-${k}`" v-html="svgs[k]"></div>
        <div class="qlv-pad"></div>
      </div>
      <slot name="banner" />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { drawLevel, levelOrder, miniGlyph, stripItems } from '@/utils/quest-sky'

const props = defineProps({
  journey: { type: Object, required: true },
  focus: { type: String, default: 'fs' },
  // Per level: { ceremony } for the Sun; { ring, liftWord, ringDone } anywhere.
  fx: { type: Object, default: () => ({}) },
  justDone: { type: String, default: '' },
  bright: { type: Boolean, default: false },
  reduced: { type: Boolean, default: false },
  pfx: { type: String, default: 'qf' },
})
const emit = defineEmits(['focus', 'star'])

const car = ref(null)
const order = computed(() => levelOrder(props.journey))
const topDown = computed(() => order.value.slice().reverse())
const at = computed(() => order.value.indexOf(props.focus))
const strip = computed(() => stripItems(props.journey).items)
const svgs = computed(() => {
  const out = {}
  for (const k of order.value) {
    const f = { nums: true, noLabels: true, ...props.fx }
    if (k !== 'su') delete f.ceremony
    out[k] = drawLevel(props.journey, k, f, `${props.pfx}${k}`)
  }
  return out
})

function step (d) {
  const n = order.value[at.value + d]
  if (n) emit('focus', n)
}

function onClick (e) {
  const t = e.target && e.target.closest ? e.target.closest('[data-key]') : null
  if (t) { emit('star', t.getAttribute('data-key')); return }
  // A tap on a dimmed level above or below brings it into focus.
  const item = e.target && e.target.closest ? e.target.closest('[data-qitem]') : null
  if (item && item.getAttribute('data-qitem') !== props.focus) emit('focus', item.getAttribute('data-qitem'))
}

// Centre the focused level; a hand scroll that settles on another focuses it.
let quietUntil = 0
let settle = null
function centre (smooth) {
  const c = car.value
  const el = c && c.querySelector(`[data-qitem="${props.focus}"]`)
  if (!c || !el) return
  quietUntil = Date.now() + (smooth ? 700 : 80)
  const top = el.offsetTop - (c.clientHeight - el.clientHeight) / 2
  try { c.scrollTo({ top, behavior: smooth && !props.reduced ? 'smooth' : 'auto' }) } catch (e) { c.scrollTop = top }
}
function onScroll () {
  if (Date.now() < quietUntil) return
  clearTimeout(settle)
  settle = setTimeout(() => {
    const c = car.value
    if (!c || !c.clientHeight) return
    const mid = c.scrollTop + c.clientHeight / 2
    let best = null; let dist = Infinity
    for (const el of c.querySelectorAll('[data-qitem]')) {
      const d = Math.abs(el.offsetTop + el.clientHeight / 2 - mid)
      if (d < dist) { dist = d; best = el.getAttribute('data-qitem') }
    }
    if (best && best !== props.focus) emit('focus', best)
  }, 140)
}
watch(() => props.focus, () => nextTick(() => centre(true)))

// The line draws in and the star pops as each one lights (the prototype's qUpdate).
let litBefore = null
function litNow () {
  const out = new Set()
  for (const k of order.value) for (const st of props.journey.sections[k].stars) if (st.done) out.add(st.key)
  return out
}
watch(() => props.journey, () => {
  const now = litNow()
  const fresh = litBefore ? [...now].filter((k) => !litBefore.has(k)) : []
  litBefore = now
  if (!fresh.length || props.reduced) return
  nextTick(() => {
    const c = car.value
    if (!c) return
    for (const key of fresh) {
      const g = c.querySelector(`.st[data-key="${key}"]`)
      if (!g) continue
      g.classList.add('pop')
      const blk = g.closest('.blk')
      if (blk) blk.querySelectorAll('line[stroke="#f0d9a3"]').forEach((l) => { l.setAttribute('pathLength', '100'); l.classList.add('drawin') })
    }
  })
}, { immediate: true })

onMounted(() => nextTick(() => centre(false)))
onUnmounted(() => clearTimeout(settle))
defineExpose({ centre })
</script>

<style>
/* The level carousel (prototype .qcar/.qitem); its svg is v-html, so unscoped.
   The sky's type and motion, as QuestSky.vue draws them. */
.qlv-car.qsky svg.sky { display: block; width: 100%; height: auto; user-select: none; -webkit-tap-highlight-color: transparent; }
.qlv-car.qsky .dsp { font-family: Cinzel, serif; font-weight: 600; }
.qlv-car.qsky .srf { font-family: 'Cormorant Garamond', Cormorant, serif; }
.qlv-car.qsky .it-s { font-family: 'Cormorant Garamond', Cormorant, serif; font-style: italic; }
.qlv-car.qsky .st { cursor: pointer; }
.qlv-car.qsky .st:focus { outline: none; }
.qlv-car.qsky .st:focus-visible .hit { stroke: #ffe7b5; stroke-width: 1.2; }
.qlv-car.qsky .pulse { animation: qsky-pulse 2.4s ease-in-out infinite; }
@keyframes qsky-pulse { 0%, 100% { opacity: 1; } 50% { opacity: .3; } }
.qlv-car.qsky .blk.bright { animation: qsky-bright 1.3s ease-out both; animation-delay: var(--d); }
@keyframes qsky-bright { 0% { filter: none; } 40% { filter: brightness(1.9) drop-shadow(0 0 6px #ffe7b5); } 100% { filter: none; } }
.qlv-car.qsky .ringagain { transform-box: fill-box; transform-origin: center; animation: qsky-ring 1.7s ease-out both; }
.qlv-car.qsky .ringagain.r2 { animation-delay: .55s; }
@keyframes qsky-ring { 0% { transform: scale(.4); opacity: .95; } 100% { transform: scale(3.4); opacity: 0; } }
.qlv-car.qsky .lift { animation: qsky-lift 3s ease-out .3s both; }
@keyframes qsky-lift { 0% { opacity: 0; transform: translateY(0); } 15% { opacity: 1; } 75% { opacity: 1; transform: translateY(-40px); } 100% { opacity: 0; transform: translateY(-56px); } }
/* The Sun's ceremony in the sky. */
.qlv-car.qsky .cer .sunup { animation: qsky-sunup 2s cubic-bezier(.2,.7,.2,1) .3s both; }
@keyframes qsky-sunup { from { transform: translateY(110px); } }
.qlv-car.qsky .cer .split { stroke-dasharray: 100; animation: qsky-draw .5s ease-in 2.2s both; }
.qlv-car.qsky .cer .halfL { animation: qsky-partL .9s ease-in 2.75s both; }
.qlv-car.qsky .cer .halfR { animation: qsky-partR .9s ease-in 2.75s both; }
@keyframes qsky-partL { to { transform: translateX(-46px); opacity: 0; } }
@keyframes qsky-partR { to { transform: translateX(46px); opacity: 0; } }
.qlv-car.qsky .cer .rays { animation: qsky-fade .8s ease-out 3.3s both; }
.qlv-car.qsky .cer .sunstars { animation: qsky-fade .8s ease-out 3.6s both; }
.qlv-car.qsky .halfL, .qlv-car.qsky .halfR, .qlv-car.qsky .sunstars, .qlv-car.qsky .rays { transform-box: view-box; }
@keyframes qsky-draw { from { stroke-dashoffset: 100; } }
@keyframes qsky-fade { from { opacity: 0; } }
@keyframes qsky-still { 0% { opacity: 0; } 15%, 80% { opacity: 1; } 100% { opacity: 0; } }
/* Reduced motion: in place, fades only. */
.qlv-car.qsky.rm .blk, .qlv-car.qsky.rm .cer *, .qlv-car.qsky.rm .pulse { animation: none !important; }
.qlv-car.qsky.rm .ringagain { display: none; }
.qlv-car.qsky.rm .lift { animation: qsky-still 3s ease-in-out both !important; }
.qlv-car.qsky.rm .cer .halfL, .qlv-car.qsky.rm .cer .halfR, .qlv-car.qsky.rm .cer .split { display: none; }
@media (prefers-reduced-motion: reduce) {
  .qlv-car.qsky .blk, .qlv-car.qsky .cer *, .qlv-car.qsky .pulse { animation: none !important; }
  .qlv-car.qsky .ringagain { display: none; }
  .qlv-car.qsky .lift { animation: qsky-still 3s ease-in-out both !important; }
  .qlv-car.qsky .cer .halfL, .qlv-car.qsky .cer .halfR, .qlv-car.qsky .cer .split { display: none; }
}

.qlv { position: relative; display: flex; flex-direction: column; min-height: 0; }
.qlv-strip { display: flex; justify-content: space-between; align-items: center; margin: 0 4px 10px; position: relative; }
.qlv-strip::before { content: ''; position: absolute; left: 16px; right: 16px; top: 50%; border-top: 1px dashed rgba(155,134,201,.35); }
.qlv-sg { position: relative; width: 40px; height: 40px; border-radius: 50%; border: 1px solid rgba(155,134,201,.3); background: #120829; display: grid; place-items: center; padding: 0; cursor: pointer; }
.qlv-sg svg { width: 26px; height: 26px; }
.qlv-sg::after { content: ''; position: absolute; inset: -4px; border-radius: 50%; }
.qlv-sg.done { border-color: rgba(240,217,163,.6); background: #1d0f38; }
.qlv-sg.cur { box-shadow: 0 0 0 3px rgba(240,217,163,.12), 0 0 12px rgba(240,217,163,.3); border-color: #f0d9a3; }
.qlv-sg.fut { opacity: .5; }
.qlv-sg.fut:has(.qlv-seed) { opacity: .8; }
.qlv-sg.focus { outline: 2px solid #ffe7b5; outline-offset: 3px; opacity: 1; }
.qlv-sg:focus-visible { outline: 2px solid #ffe7b5; outline-offset: 3px; }
.qlv-seed { position: absolute; top: 3px; right: 3px; width: 7px; height: 7px; border-radius: 50%; background: #ffe7b5; box-shadow: 0 0 6px #ffe7b5; }
.qlv-sg.bright { animation: qlv-sg-bright 1.1s ease-out both; }
@keyframes qlv-sg-bright { 0% { box-shadow: 0 0 0 0 rgba(255,231,181,0); } 40% { box-shadow: 0 0 22px 8px rgba(255,231,181,.75); background: #3a2560; } 100% { box-shadow: 0 0 0 0 rgba(255,231,181,0); } }
.qlv-stage { position: relative; flex: 1 1 auto; min-height: 0; }
.qlv-arrows { position: absolute; right: 12px; top: 14px; display: flex; flex-direction: column; gap: 8px; z-index: 5; }
.qlv-arrows button { width: 36px; height: 36px; border-radius: 50%; border: 1px solid rgba(240,217,163,.4); background: rgba(20,10,40,.85); color: #f0d9a3; font-size: 14px; cursor: pointer; }
.qlv-arrows button:disabled { opacity: .3; cursor: default; }
.qlv-car { position: relative; height: 560px; max-height: 62vh; overflow-y: auto; scroll-snap-type: y mandatory; scrollbar-width: none; border-radius: 18px;
  border: 1px solid rgba(201,169,78,.35); background: radial-gradient(120% 70% at 50% 100%, #22114a 0%, #120829 60%, #0b0419 100%); }
.qlv-car::-webkit-scrollbar { display: none; }
.qlv-pad { height: 90px; }
.qlv-item { scroll-snap-align: center; height: 380px; display: flex; align-items: center; justify-content: center; transition: opacity .45s, transform .45s, filter .45s;
  opacity: .28; transform: scale(.84); filter: saturate(.5); }
.qlv-item.focus { opacity: 1; transform: none; filter: none; }
.qlv-item svg.lvl { display: block; width: min(100%, 520px); height: auto; max-height: 370px; overflow: visible; user-select: none; -webkit-tap-highlight-color: transparent; }
.qlv-item .drawin { stroke-dasharray: 100; animation: qlv-draw .9s cubic-bezier(.4,.1,.2,1) both; }
@keyframes qlv-draw { from { stroke-dashoffset: 100; } to { stroke-dashoffset: 0; } }
.qlv-item .st.pop path { transform-box: fill-box; transform-origin: center; animation: qlv-pop .7s cubic-bezier(.2,.9,.3,1.4) both; }
@keyframes qlv-pop { 0% { transform: scale(.2); } 60% { transform: scale(1.6); } 100% { transform: none; } }
.qlv-item.justdone svg { animation: qlv-bright 1.6s ease-out both; }
@keyframes qlv-bright { 0% { filter: none; } 35% { filter: brightness(1.9) drop-shadow(0 0 10px #ffe7b5); } 100% { filter: none; } }
.qlv.rm .qlv-item, .qlv.rm .qlv-item * { transition: none !important; animation: none !important; }
@media (prefers-reduced-motion: reduce) {
  .qlv-item, .qlv-item * { transition: opacity .3s !important; animation: none !important; }
  .qlv-sg.bright { animation: none; }
}
@media (max-width: 899px) {
  .qlv-car { height: 380px; }
  .qlv-item { height: 280px; }
  .qlv-pad { height: 50px; }
  .qlv-item svg.lvl { max-height: 270px; }
  .qlv-sg { width: 36px; height: 36px; }
  .qlv-strip { margin: 0 0 10px; }
}
</style>
